import {
  DateStringOrNull,
  MAIN_PACE_TASK_IDs,
} from '@symfa-inc/providence-verizon-types';
import { transformFields } from '@core/utils/methods';
import { browseReducer } from '@core/utils/methods/browse-sorter-reducer';
import { createReducer, on } from '@core/utils/store';
import {
  SCOPING_INFO_TRANSFORM_FIELDS,
  SCOPING_PROJECT_INFO_TRANSFORM_FIELDS,
  SCOPING_SECTORS,
} from '@models/constants';
import { ModalMainTypes } from '@models/enums';
import {
  EngineeringWorkflowUpdateData,
  EquipmentModal,
  IdAndValue,
  LeasingInformation,
  OtherEquipmentData,
  PaginatedResponse,
  PayloadAndState,
  ProjectDetails,
  ProjectPaceTask,
  ScopingAssociatedProjectsTabData,
  ScopingBrowse,
  ScopingBrowseResponse,
  ScopingEquipmentTabData,
  ScopingInfo,
  ScopingSummaryData,
  ScopingSummaryTabData,
  SectorEquipment,
  SiteAuditInformation,
  SiteResponse,
  UpdateScopingEquipmentData,
} from '@models/interfaces';
import { LeasingEquipmentData, SectorDataState } from '@models/types';
import { ScopingActions } from '@store/actions';
import {
  equipmentDuplicatesCombiner,
  getEquipmentTotalsFromScoping,
  getOtherEquipmentInfo,
  getScopingSectorsFromUpdateData,
  getScopingSectorsInfo,
  sortEquipments,
} from '../helpers';

type ScopingProjectInfo = Omit<
  ProjectDetails,
  'id' | 'site' | 'paceTasks' | 'fuzeIDs'
>;

// TODO: move to selectors
export interface EngineeringScopingState {
  scopingBrowseData: PaginatedResponse<ScopingBrowse>;
  currentProjectId: string;
  currentModalType: ModalMainTypes;
  scopingSiteInfo: SiteResponse;
  projectType: IdAndValue;
  scopingProjectInfo: ScopingProjectInfo;
  scopingLeasingInfo: LeasingInformation;
  scopingInfo: Omit<ScopingInfo, 'otherEquipments' | 'scopingSectors'>;
  scopingSiteAuditData: SiteAuditInformation;
  scopingEngineeringData: EngineeringWorkflowUpdateData;
  scheduledScopingDate?: DateStringOrNull;
  mountMappingOrdered?: DateStringOrNull;
  mountMappingReceived?: DateStringOrNull;
  mountAnalysisOrdered?: DateStringOrNull;
  failingMountAnalysisReceived?: DateStringOrNull;
  passingMountAnalysisReceived?: DateStringOrNull;
  prelimCDsOrdered?: DateStringOrNull;
  prelimCDsReceived?: DateStringOrNull;
  finalCDsReceived?: DateStringOrNull;
  otherEquipments: OtherEquipmentData[];
  scopingSectors: SectorDataState;
  isFetching: boolean;
  errors: boolean;
  equipmentTypeNames: string[];
  leasingEquipmentData: LeasingEquipmentData;
  scopingAssociatedProjects: SiteResponse['projects'];
  loadingTabs: Record<string, boolean>;
  loadedTabs: Record<string, boolean>;
  tabErrors: Record<string, boolean>;
}

const initialState: EngineeringScopingState = {
  scopingBrowseData: { items: [], total: 0 },
  currentProjectId: '',
  currentModalType: ModalMainTypes.Add,
  scopingSiteInfo: {} as SiteResponse,
  scopingProjectInfo: {} as ScopingProjectInfo,
  projectType: {} as IdAndValue,
  scopingLeasingInfo: {} as LeasingInformation,
  scopingInfo: {} as Omit<ScopingInfo, 'otherEquipments' | 'scopingSectors'>,
  scopingSiteAuditData: {} as SiteAuditInformation,
  scopingEngineeringData: {
    id: '',
    regulatoryCompliance: {},
    RFDSPhase1: {},
  } as EngineeringWorkflowUpdateData,
  scheduledScopingDate: undefined,
  mountMappingOrdered: undefined,
  mountMappingReceived: undefined,
  mountAnalysisOrdered: undefined,
  failingMountAnalysisReceived: undefined,
  passingMountAnalysisReceived: undefined,
  prelimCDsOrdered: undefined,
  prelimCDsReceived: undefined,
  finalCDsReceived: undefined,
  otherEquipments: [],
  scopingSectors: SCOPING_SECTORS,
  isFetching: false,
  errors: false,
  equipmentTypeNames: [],
  leasingEquipmentData: {
    priorTableData: [],
    curPriorTableData: [],
    scopingTableData: [],
    finalTableData: [],
    curFinalTableData: [],
    scopingNotes: null,
    priorLeasingNote: null,
    finalLeasingNote: null,
  },
  scopingAssociatedProjects: [],
  loadingTabs: {},
  loadedTabs: {},
  tabErrors: {},
};

const equipmentTypeNames = new Set<string>();

export const reducer = createReducer(
  initialState,
  // GET SCOPING BROWSE DATA
  on(
    ScopingActions.getScopingBrowseDataAction,
    browseReducer('scopingBrowseData'),
  ),
  // UPDATE CURRENT MODAL TYPE
  on(
    ScopingActions.updateCurrentModalTypeAction,
    ({
      payload: currentModalType,
    }: PayloadAndState<ModalMainTypes, EngineeringScopingState>) => ({
      currentModalType,
    }),
  ),
  // UPDATE SCOPING BROWSE DATA
  on(
    ScopingActions.updateScopingBrowseDataAction,
    ({
      payload,
    }: PayloadAndState<
      PaginatedResponse<ScopingBrowseResponse>,
      EngineeringScopingState
    >) => ({
      scopingBrowseData: {
        ...payload,

        items: payload.items.map(({ categories, ...item }) => ({
          ...item,
          fuzeIDs: categories,
        })),
      },
    }),
  ),
  // SET TAB LOADING STATE
  on(
    ScopingActions.setTabLoading,
    ({
      payload,
      state,
    }: PayloadAndState<
      { tabName: string; isLoading: boolean },
      EngineeringScopingState
    >) => ({
      ...state,
      loadingTabs: {
        ...state.loadingTabs,
        [payload.tabName]: payload.isLoading,
      },
      tabErrors: {
        ...state.tabErrors,
        [payload.tabName]: false,
      },
    }),
  ),
  // SET TAB LOADED STATE
  on(
    ScopingActions.setTabLoaded,
    ({
      payload,
      state,
    }: PayloadAndState<
      { tabName: string; isLoaded: boolean },
      EngineeringScopingState
    >) => ({
      ...state,
      loadingTabs: {
        ...state.loadingTabs,
        [payload.tabName]: false,
      },
      loadedTabs: {
        ...state.loadedTabs,
        [payload.tabName]: payload.isLoaded,
      },
      tabErrors: {
        ...state.tabErrors,
        [payload.tabName]: false,
      },
    }),
  ),
  // SET TAB ERROR STATE
  on(
    ScopingActions.setTabError,
    ({
      payload,
      state,
    }: PayloadAndState<
      { tabName: string; hasError: boolean },
      EngineeringScopingState
    >) => ({
      ...state,
      loadingTabs: {
        ...state.loadingTabs,
        [payload.tabName]: false,
      },
      tabErrors: {
        ...state.tabErrors,
        [payload.tabName]: payload.hasError,
      },
    }),
  ),
  // RESET TABS DATA
  on(
    ScopingActions.resetTabsData,
    ({ state }: PayloadAndState<unknown, EngineeringScopingState>) => ({
      ...state,
      loadingTabs: {},
      loadedTabs: {},
      tabErrors: {},
    }),
  ),
  // GET SCOPING SUMMARY
  on(
    ScopingActions.getScopingSummaryAction,
    ({
      payload: {
        site,
        id,
        leasing: scopingLeasingInfo,
        siteAudit: scopingSiteAuditData,
        paceTasks,
        scoping: scopingPayload,
        ...projectData
      },
      state,
    }: PayloadAndState<
      Omit<ScopingSummaryTabData, 'categories' | 'RAD' | 'providenceAgent'>,
      EngineeringScopingState
    >) => {
      const scopingData = scopingPayload ?? {};
      const structuralOrdered = paceTasks.find(
        (paceTask: ProjectPaceTask) =>
          paceTask?.id === MAIN_PACE_TASK_IDs.structuralOrdered,
      )?.projectPaceTaskData?.actualDate;
      const passingStructuralReceived = paceTasks.find(
        (paceTask: ProjectPaceTask) =>
          paceTask?.id === MAIN_PACE_TASK_IDs.passingStructuralReceived,
      )?.projectPaceTaskData?.actualDate;

      return {
        ...state,
        scopingSiteInfo: transformFields<SiteResponse>(site as SiteResponse, [
          'towerOwner',
          'riskCategory',
        ]),
        scopingProjectInfo: transformFields<ScopingProjectInfo>(
          projectData as ScopingProjectInfo,
          SCOPING_PROJECT_INFO_TRANSFORM_FIELDS,
        ),
        projectType: projectData.projectType,
        scopingInfo: {
          ...state.scopingInfo,
          ...transformFields<
            Omit<ScopingInfo, 'otherEquipments' | 'scopingSectors'>
          >(
            scopingData as Omit<
              ScopingInfo,
              'otherEquipments' | 'scopingSectors'
            >,
            SCOPING_INFO_TRANSFORM_FIELDS,
          ),
        },
        scheduledScopingDate: paceTasks.find(
          (paceTask: ProjectPaceTask) =>
            paceTask?.id === MAIN_PACE_TASK_IDs.scopingComplete,
        )?.projectPaceTaskData?.forecastedDate,
        scopingSiteAuditData: scopingSiteAuditData as SiteAuditInformation,
        scopingLeasingInfo: {
          ...scopingLeasingInfo,
          structural: {
            ...scopingLeasingInfo?.structural,
            structuralOrdered: structuralOrdered ?? null,
            passingStructuralReceived: passingStructuralReceived ?? null,
          },
        } as unknown as LeasingInformation,
        currentProjectId: id,
        loadingTabs: { ...state.loadingTabs, summary: false },
        loadedTabs: { ...state.loadedTabs, summary: true },
        tabErrors: { ...state.tabErrors, summary: false },
      };
    },
  ),
  // GET SCOPING EQUIPMENT
  on(
    ScopingActions.getScopingEquipmentAction,
    ({
      payload: {
        id,
        leasing: scopingLeasingInfo,
        scoping: scopingPayload,
        engineering: engineeringPayload,
      },
      state,
    }: PayloadAndState<
      Omit<ScopingEquipmentTabData, 'RAD'>,
      EngineeringScopingState
    >) => {
      const engineeringData = engineeringPayload ?? {};
      const scopingData = scopingPayload ?? {};
      const leasing = scopingLeasingInfo ?? {};
      const { otherEquipments, scopingSectors, ...restScopingData } =
        scopingData;
      const { RFDSPhase1, ...restEngineeringData } = engineeringData;

      const otherEquipmentInfo = getOtherEquipmentInfo(otherEquipments ?? []);

      const scopingSectorsInfo = getScopingSectorsInfo(scopingSectors ?? []);

      return {
        ...state,
        scopingInfo: {
          ...state.scopingInfo,
          ...transformFields<
            Omit<ScopingInfo, 'otherEquipments' | 'scopingSectors'>
          >(
            restScopingData as Omit<
              ScopingInfo,
              'otherEquipments' | 'scopingSectors'
            >,
            SCOPING_INFO_TRANSFORM_FIELDS,
          ),
        },
        scopingEngineeringData: {
          ...state.scopingEngineeringData,
          ...restEngineeringData,
          RFDSPhase1: RFDSPhase1
            ? transformFields(RFDSPhase1, ['RFDSStatus'])
            : state.scopingEngineeringData.RFDSPhase1,
        },
        scopingLeasingInfo: {
          ...state.scopingLeasingInfo,
          ...leasing,
        } as LeasingInformation,
        currentProjectId: id,
        otherEquipments: otherEquipmentInfo?.otherEquipments || [],
        equipmentTypeNames: [
          ...(otherEquipmentInfo?.equipmentTypeNames || []),
          ...(scopingSectorsInfo?.equipmentTypeNames || []),
          ...equipmentTypeNames,
        ],
        scopingSectors:
          scopingSectorsInfo?.scopingSectors || initialState.scopingSectors,
        leasingEquipmentData: {
          ...state.leasingEquipmentData,
          scopingTableData: sortEquipments(
            getEquipmentTotalsFromScoping(
              equipmentTypeNames,
              scopingData as ScopingInfo,
              'ScopingEquipment',
            ),
          ),
          scopingNotes: restScopingData?.scopingTowerNote ?? null,
        },
        loadingTabs: { ...state.loadingTabs, equipment: false },
        loadedTabs: { ...state.loadedTabs, equipment: true },
        tabErrors: { ...state.tabErrors, equipment: false },
      };
    },
  ),
  // GET SCOPING ASSOCIATED PROJECTS
  on(
    ScopingActions.getScopingAssociatedProjectsAction,
    ({
      payload,
      state,
    }: PayloadAndState<
      ScopingAssociatedProjectsTabData,
      EngineeringScopingState
    >) => ({
      ...state,
      scopingAssociatedProjects: payload.projects,
      scopingSiteInfo: {
        ...state.scopingSiteInfo,
        ...payload,
      } as SiteResponse,
      loadingTabs: { ...state.loadingTabs, associatedProjects: false },
      loadedTabs: { ...state.loadedTabs, associatedProjects: true },
      tabErrors: { ...state.tabErrors, associatedProjects: false },
    }),
  ),
  // UPDATE SCOPING SUMMARY
  on(
    ScopingActions.updateScopingSummaryAction,
    ({
      payload: {
        projectInformation: projectInfoPayload,
        preScopingAssessment: preScopingPayload,
      },
      state,
    }: PayloadAndState<ScopingSummaryData, EngineeringScopingState>) => {
      const projectData = projectInfoPayload ?? {};
      const scopingData = preScopingPayload ?? {};
      const { AEVendor, AEMountVendor, ...restProjectData } = projectData;
      const {
        scheduledScopingDate,
        mountMappingOrdered,
        mountMappingReceived,
        mountAnalysisOrdered,
        failingMountAnalysisReceived,
        passingMountAnalysisReceived,
        prelimCDsOrdered,
        prelimCDsReceived,
        finalCDsReceived,
        ...restScopingData
      } = scopingData;

      return {
        scopingProjectInfo: {
          ...state.scopingProjectInfo,
          ...restProjectData,
        },
        scopingInfo: {
          ...state.scopingInfo,
          ...restScopingData,
          AEVendor,
          AEMountVendor,
        },
        scheduledScopingDate,
        mountMappingOrdered,
        mountMappingReceived,
        mountAnalysisOrdered,
        failingMountAnalysisReceived,
        passingMountAnalysisReceived,
        prelimCDsOrdered,
        prelimCDsReceived,
        finalCDsReceived,
      };
    },
  ),
  // UPDATE SCOPING EQUIPMENT
  on(
    ScopingActions.updateScopingEquipmentAction,
    ({
      payload: {
        scopingNotes: scopingNotesPayload,
        scopingInformation: scopingInfoPayload,
        otherEquipments,
        sectors,
      },
      state,
    }: PayloadAndState<
      UpdateScopingEquipmentData,
      EngineeringScopingState
    >) => {
      const scopingInfoData = scopingInfoPayload ?? {};
      const scopingNotesInfo = scopingNotesPayload ?? {};
      const {
        prelimRFDSReceivedDate,
        isMicrowaveToBeRemoved,
        isCraneRequired,
        craneHeight,
        mountModelReplacements,
      } = scopingInfoData;
      const { RFDSStatus, ...scopingNotes } = scopingNotesInfo;

      const sectorsEquipments = sectors.flatMap(sector =>
        sector.sectorEquipmentTypes.flatMap(sectorEquipmentType =>
          sectorEquipmentType.equipments
            .filter(
              (equipmentItem: SectorEquipment) => !!equipmentItem.equipment,
            )
            .map((equipmentItem: SectorEquipment) => {
              const { position, ...equipment } = equipmentItem;

              return {
                ...equipment,
                equipmentType: sectorEquipmentType.equipmentType,
              } as EquipmentModal;
            }),
        ),
      );

      return {
        scopingInfo: {
          ...state.scopingInfo,
          ...scopingNotes,
          ...(isMicrowaveToBeRemoved !== undefined
            ? { isMicrowaveToBeRemoved }
            : {}),
          ...(mountModelReplacements?.length ? { mountModelReplacements } : {}),
        },
        scopingEngineeringData: {
          RFDSPhase1: {
            ...state.scopingEngineeringData.RFDSPhase1,
            ...(prelimRFDSReceivedDate !== undefined
              ? { prelimRFDSReceivedDate }
              : {}),
            ...(RFDSStatus !== undefined && { RFDSStatus }),
          },
          regulatoryCompliance: {
            ...state.scopingEngineeringData.regulatoryCompliance,
            ...(isCraneRequired !== undefined ? { isCraneRequired } : {}),
            ...(craneHeight ? { craneHeight } : {}),
          },
        },
        leasingEquipmentData: {
          ...state.leasingEquipmentData,
          scopingTableData: equipmentDuplicatesCombiner([
            ...sectorsEquipments,
            ...(otherEquipments as EquipmentModal[]),
          ]),
        },
        otherEquipments,
        scopingSectors: getScopingSectorsFromUpdateData(sectors),
      };
    },
  ),
  // UPDATE SCOPING SECTORS
);
